/*eslint-disable */
import React, { useEffect, ComponentType, FC, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import { useLocation } from 'Utils/router';
const URI = require('urijs');
import TourStore from '@store/tour';
import SSOStore from '@store/SSO';
import YouvisitAnalyticsStore from '@store/youvisitAnalytics';
import PageLoader from 'Components/loader/pageLoader';
import { GeneralError } from 'Components/helpers';
import EMBED_TYPE from '@constants/embedTypeConstants';
import { IRootState } from '@store/root/rootState';
import { APPILY_QUERY } from './gqlQuery';

/**
 * Generate Initial Tour Model
 * - be explicit with keys
 */
const getInitializeParamsFromUrl = (
  routeLocation: any
): { tourModel: any; analyticsModel: any } => {
  const hasSearch =
    window.location.hash.indexOf('?') > 0
      ? window.location.hash.split('?')[1]
      : '';

  //The linter prefers this way, instead of nested conditionals
  let search = window.location.search ? window.location.search : hasSearch;
  search = routeLocation.search ? routeLocation.search : search;

  // Traditional RouteParams are not loaded at this time, so we have to do it ourselves
  const routeParams = routeLocation.pathname?.split('/');
  const urlParams = URI.parseQuery(search);

  const isValidRouteParams =
    routeParams && routeParams.length > 3 && 'registration-app' !== routeParams[1];

  let instId = false;
  if (isValidRouteParams) instId = routeParams[1];
  if (!instId) instId = urlParams.inst_id;
  if (!instId) instId = urlParams.instId;
  let locId = false;
  if (isValidRouteParams) locId = routeParams[2];
  if (!locId) locId = urlParams.loc_id;
  if (!locId) locId = urlParams.locId;
  let source = urlParams.source;
  if (!source) source = 'web';
  let platform = urlParams.source;
  if (!platform && urlParams.pl) platform = urlParams.pl;
  if (!platform && urlParams.platform) platform = urlParams.platform;
  if (!platform) platform = 'w';
  let originalSource = urlParams.originalSource;
  if (!originalSource) originalSource = urlParams.original_source;
  if (!originalSource && source) originalSource = source;
  let title = null;
  if (!title) title = urlParams.title;
  let subtitle = null;
  if (!subtitle) subtitle = urlParams.subtitle;
  let successTitle = null;
  if (!successTitle) successTitle = urlParams.successTitle;
  let successSubtitle = null;
  if (!successSubtitle) successSubtitle = urlParams.successSubtitle;
  let successDescription = null;
  if (!successDescription) successDescription = urlParams.successDescription;
  let fromSearch = null;
  if (!fromSearch) fromSearch = urlParams.fromSearch;
  let email = null;
  if (!email) email = urlParams.email;
  let firstname = null;
  if (!firstname) firstname = urlParams.firstName;
  let lastname = null;
  if (!lastname) lastname = urlParams.lastName;
  let token = null;
  if (!token) token = urlParams.token;
  let module = null;
  if (!module) module = urlParams.module_id;
  //This will set the value of standalone.  It takes into account null, undefined, a numeric string and a number.
  const isStandalone =
    urlParams.standalone === 'true' ||
    urlParams.standalone === '1' ||
    urlParams.standalone === 1
      ? true
      : false;
  let embedType = urlParams.embed_type ? urlParams.embed_type : false;
  if (!embedType)
    embedType = urlParams.embedType
      ? urlParams.embedType
      : EMBED_TYPE.EMBED_TYPE_STANDALONE;

  //TODO: Move use redux....
  const tourModel = {
    instId,
    locId,
    tourid: urlParams.tourid,
    platform,
    onCampus: urlParams.oncampus ? urlParams.oncampus : false,
    isHover: urlParams.isHover,
    isStandalone,
    embedType,
    title,
    subtitle,
    successTitle,
    successSubtitle,
    successDescription,
    fromSearch,
    isLoading: false,
    typeid: urlParams.typeid,
    mode: urlParams.mode,
    step: urlParams.step,
    formType: urlParams.formType
  };

  const analyticsModel = {
    userkey: urlParams.userkey ? urlParams.userkey : urlParams.userKey,
    sessionId: urlParams.sessionid ? urlParams.sessionid : '0',
    source,
    originalSource,
    isReady: true,
    token,
    module,
  };

  return { tourModel, analyticsModel };
};

interface Props {
  children: any;
}

const TourInitializer: FC<any> = ({ children }: Props) => {
  const dispatch = useDispatch();
  const routeLocation = useLocation();
  const tourModel = useSelector((state: IRootState) => state.tour);

  const { data: appilyData, loading } = useQuery(APPILY_QUERY, {
    variables: {
      instId: tourModel.instId,
      locId: tourModel.locId
    },
    skip: !tourModel?.instId || !tourModel?.locId,
  })

  useEffect(() => {
    const data = getInitializeParamsFromUrl(routeLocation);
    dispatch(TourStore.actions.initializeTourWithParams(data.tourModel));

    if (!data.tourModel.isStandalone) {
      // if standalone we should init based on passed params
      dispatch(
        YouvisitAnalyticsStore.actions.setYouVisitAnalyticsConfig({
          isReady: true,
          analytics: data.analyticsModel,
        })
      );
    }
  }, [routeLocation]);

  useEffect(() => {
    if (appilyData) {
      dispatch(TourStore.actions.initializeTourWithParams({ 
        cappexId: appilyData.institutions?.cx_inst_id, 
        flexRegId: appilyData.locations?.cx_flex_reg_id,
        heapAnalytics: appilyData.institutions?.heap_analytics,
        locationModules: appilyData.locations?.modules || []
      }));
    }
  }, [appilyData])

  useEffect(() => {
    // Get SSO data from college search window message //
    const data = getInitializeParamsFromUrl(routeLocation);
    const getSSOData = (e: any) => {
      if (e.data.message === 'sendSSOData') {
        const userData = {
          firstname: e.data.value.firstName,
          lastname: e.data.value.lastName,
          email: e.data.value.email,
        };
        dispatch(SSOStore.actions.initializeSSOWithParams(userData));
        window.removeEventListener('message', getSSOData);
      }
    };
    if (data.tourModel.fromSearch) {
      window.addEventListener('message', getSSOData);
      window.parent.postMessage({ message: 'getSSOData' }, '*');
    }
    return () => {
      window.removeEventListener('message', getSSOData);
    };
  }, []);

  //Are we still parsing data to set redux?
  if (tourModel.isLoading || loading) return <PageLoader />;
  else if (!tourModel.instId)
    return <GeneralError message="Failed to initialize form configuration." />;
  return children;
};

export default TourInitializer;
try {
    // @ts-ignore
    TourInitializer.displayName = "TourInitializer";
    // @ts-ignore
    TourInitializer.__docgenInfo = { "description": "", "displayName": "TourInitializer", "props": {} };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/hoc/TourInitializer/TourInitializer.tsx#TourInitializer"] = { docgenInfo: TourInitializer.__docgenInfo, name: "TourInitializer", path: "src/hoc/TourInitializer/TourInitializer.tsx#TourInitializer" };
}
catch (__react_docgen_typescript_loader_error) { }